import React, {useState} from "react";
import {Box, Card, CardContent, CircularProgress} from "@mui/material";
import LeagueSpartanText from "../../typography/LeagueSpartanText";
import CervinoText from "../../typography/CervinoText";
import { BACKGROUND_DESIGNS } from "./backgroundDesigns";
import SMButtonBrown from "../../navigation/SMButtonBrown";

function BackgroundDesignLayer({ backgroundDesign }) {
    const Design = BACKGROUND_DESIGNS[backgroundDesign];
    if (!Design) return null;
    return <Design />;
}

export function ProductCard({
                                name,
                                description,
                                imageSrc,
                                imageAlt = "",
                                backgroundColor = "#E9ECEF",
                                backgroundDesign = 0,
                                claims = [],
                                minHeight = 560,
                                product,
                                onViewProduct,
                            }) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    return (
        <Card
            elevation={0}
            sx={{
                width: "100%",
                borderRadius: 4,
                overflow: "hidden",
                minHeight,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
            }}
        >
            {/* Top media area (standardized height) */}
            <Box
                sx={{
                    backgroundColor,
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    height: { xs: 300, sm: 340, md: 360, lg: 380, xl: 450 },
                }}
            >
                <BackgroundDesignLayer backgroundDesign={backgroundDesign} />

                {/* Loader (shown while image is loading) */}
                {!imageLoaded && !imageError && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                        }}
                    >
                        <CircularProgress size={100} thickness={3} color={"secondary"} />
                    </Box>
                )}

                {/* Product image */}
                {!imageError && (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={imageAlt}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            height: { xs: 300, sm: 340, md: 360, lg: 380, xl: 450 },
                            width: "auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                            display: "block",

                            // 👇 smooth fade-in
                            opacity: imageLoaded ? 1 : 0,
                            transition: "opacity 300ms ease",
                        }}
                    />
                )}

                {/* Optional fallback if image fails */}
                {imageError && (
                    <Box
                        sx={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                        }}
                    >
                        <CervinoText sx={{color: "white"}}>Image unavailable</CervinoText>
                    </Box>
                )}
            </Box>

            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: 3,
                    textAlign: "left",
                    flex: "1 1 auto",
                }}
            >
                {/* Title + Description (consistent) */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <CervinoText sx={{ fontWeight: 900, letterSpacing: 1, fontSize: 20 }}>
                        {name}
                    </CervinoText>

                    <LeagueSpartanText
                        sx={{
                            fontWeight: 400,
                            color: "#000000",
                            lineHeight: 1.2,
                            fontSize: 20,
                            textAlign: "left",
                        }}
                    >
                        {description}
                    </LeagueSpartanText>
                </Box>

                {/* Claims */}
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    {(claims || []).map((claim, idx) => (
                        <Box
                            key={`${claim?.alt || "claim"}-${idx}`}
                            sx={{
                                flex: "1 1 0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 0,
                                height: 100,
                            }}
                            title={claim?.alt || ""}
                        >
                            <Box
                                component="img"
                                src={claim?.src}
                                alt={claim?.alt || ""}
                                sx={{
                                    width: "100%",
                                    maxWidth: 100,
                                    height: "auto",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </Box>
                    ))}
                </Box>

                {/* Button */}
                <Box sx={{ mt: "auto" }}>
                    <SMButtonBrown
                        fullWidth={true}
                        sx={{ fontSize: 20 }}
                        onClick={() => onViewProduct?.(product)}
                    >
                        VIEW PRODUCT
                    </SMButtonBrown>
                </Box>
            </CardContent>
        </Card>
    );
}
